import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { payslipAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../../lib/utils';
import { FileText, Eye, Download, Loader2 } from 'lucide-react';

const MyPayslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const fetchPayslips = useCallback(async () => {
    try {
      const response = await payslipAPI.getMyPayslips();
      setPayslips(response.data);
    } catch (err) {
      toast.error('Failed to load payslips');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  const handleViewPayslip = async (payslipId) => {
    try {
      const response = await payslipAPI.getPayslip(payslipId);
      setSelectedPayslip(response.data);
      setDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load payslip details');
    }
  };

  const handleDownloadPdf = async (payslipId, employeeName, periodEnd) => {
    setDownloading(payslipId);
    try {
      const response = await payslipAPI.downloadPdf(payslipId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${employeeName.replace(/\s+/g, '_')}_${periodEnd}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (err) {
      toast.error('Failed to download payslip');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Payslips">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate totals
  const totalEarnings = payslips.reduce((sum, r) => sum + (r.gross_pay || 0), 0);
  const totalDeductions = payslips.reduce((sum, r) => sum + (r.total_deductions || 0), 0);
  const totalNetPay = payslips.reduce((sum, r) => sum + (r.net_pay || 0), 0);

  return (
    <DashboardLayout 
      title="My Payslips" 
      subtitle="View and download your payslips"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <p className="text-lg font-bold">{formatCurrency(totalEarnings)}</p>
            <p className="text-xs text-muted-foreground">Total Earnings (YTD)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-lg font-bold text-red-600">{formatCurrency(totalDeductions)}</p>
            <p className="text-xs text-muted-foreground">Total Deductions (YTD)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-lg font-bold text-emerald-600">{formatCurrency(totalNetPay)}</p>
            <p className="text-xs text-muted-foreground">Total Net Pay (YTD)</p>
          </CardContent>
        </Card>
      </div>

      {/* Payslips Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Payslips
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pay Period</TableHead>
                  <TableHead className="hidden sm:table-cell">Pay Date</TableHead>
                  <TableHead>Gross Pay</TableHead>
                  <TableHead className="hidden md:table-cell">Deductions</TableHead>
                  <TableHead>Net Pay</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No payslips available yet
                    </TableCell>
                  </TableRow>
                ) : (
                  payslips.map((payslip) => (
                    <TableRow key={payslip.id} data-testid={`payslip-row-${payslip.id}`}>
                      <TableCell className="font-medium">
                        <div className="text-sm">
                          {formatDate(payslip.period_start)} - {formatDate(payslip.period_end)}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {formatDate(payslip.pay_date)}
                      </TableCell>
                      <TableCell>{formatCurrency(payslip.gross_pay)}</TableCell>
                      <TableCell className="hidden md:table-cell text-red-600">
                        {formatCurrency(payslip.total_deductions)}
                      </TableCell>
                      <TableCell className="font-semibold text-emerald-600">
                        {formatCurrency(payslip.net_pay)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewPayslip(payslip.id)}
                            data-testid={`view-payslip-${payslip.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadPdf(payslip.id, payslip.employee_name, payslip.period_end)}
                            disabled={downloading === payslip.id}
                            data-testid={`download-payslip-${payslip.id}`}
                          >
                            {downloading === payslip.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payslip Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
          </DialogHeader>
          {selectedPayslip && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-secondary rounded-lg">
                <p className="text-sm text-muted-foreground">Pay Period</p>
                <p className="font-semibold">
                  {formatDate(selectedPayslip.period_start)} - {formatDate(selectedPayslip.period_end)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pay Date: {formatDate(selectedPayslip.pay_date)}
                </p>
              </div>
              
              {/* Earnings */}
              <div>
                <h4 className="font-semibold mb-2 text-emerald-700">Earnings</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Basic Pay</span>
                    <span>{formatCurrency(selectedPayslip.basic_pay)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Overtime Pay</span>
                    <span>{formatCurrency(selectedPayslip.overtime_pay)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Holiday Pay</span>
                    <span>{formatCurrency(selectedPayslip.holiday_pay)}</span>
                  </div>
                  {selectedPayslip.pay_items?.filter(pi => !pi.is_deduction).map((pi, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{pi.description}</span>
                      <span>{formatCurrency(pi.amount)}</span>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Gross Pay</span>
                    <span>{formatCurrency(selectedPayslip.gross_pay)}</span>
                  </div>
                </div>
              </div>
              
              {/* Government Contributions */}
              <div>
                <h4 className="font-semibold mb-2 text-red-700">Government Contributions</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>SSS</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.sss_employee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>PhilHealth</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.philhealth_employee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pag-IBIG</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.pagibig_employee)}</span>
                  </div>
                </div>
              </div>

              {/* Tax */}
              <div>
                <h4 className="font-semibold mb-2 text-amber-700">Withholding Tax</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Taxable Income</span>
                    <span>{formatCurrency(selectedPayslip.taxable_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Withholding Tax</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.withholding_tax)}</span>
                  </div>
                </div>
              </div>
              
              {/* Other Deductions */}
              {selectedPayslip.other_deductions?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Other Deductions</h4>
                  <div className="space-y-2 text-sm">
                    {selectedPayslip.other_deductions.map((d, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{d.description}</span>
                        <span className="text-red-600">-{formatCurrency(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />
              
              {/* Net Pay */}
              <div className="flex justify-between text-lg font-bold">
                <span>Net Pay</span>
                <span className="text-emerald-600">{formatCurrency(selectedPayslip.net_pay)}</span>
              </div>

              {/* Attendance Summary */}
              <div className="p-3 bg-muted rounded-lg text-xs">
                <p className="font-medium mb-1">Attendance Summary</p>
                <div className="grid grid-cols-3 gap-2 text-muted-foreground">
                  <span>Days Worked: {selectedPayslip.days_worked}</span>
                  <span>Days Absent: {selectedPayslip.days_absent}</span>
                  <span>Days on Leave: {selectedPayslip.days_on_leave}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-muted-foreground mt-1">
                  <span>Total Hours: {selectedPayslip.total_hours_worked?.toFixed(1)}</span>
                  <span>OT Hours: {selectedPayslip.total_overtime_hours?.toFixed(1)}</span>
                </div>
              </div>

              {/* Download Button */}
              <Button 
                className="w-full"
                onClick={() => handleDownloadPdf(selectedPayslip.id, selectedPayslip.employee_name, selectedPayslip.period_end)}
                disabled={downloading === selectedPayslip.id}
              >
                {downloading === selectedPayslip.id ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default MyPayslips;
